import React, { useEffect, lazy, Suspense } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { MotionConfig, AnimatePresence, motion } from "framer-motion";
import ReactGA from "react-ga4";
import Entry from "./utils/clientEntry";
import ScrollToTop from "./utils/ScrollToTop";
import HomePage from "./pages/HomePage";
import CookieBanner from "./components/ui/CookieBanner";
import "@fontsource/montserrat/300.css";
import "@fontsource/montserrat/400.css";
import "@fontsource/montserrat/500.css";
import "@fontsource/montserrat/600.css";
import "@fontsource/montserrat/700.css";
import "@fontsource/cormorant-garamond/400.css";
import "@fontsource/cormorant-garamond/500.css";
import "@fontsource/cormorant-garamond/600.css";
import "@fontsource/cormorant-garamond/400-italic.css";
import "@fontsource/dm-serif-text/400.css";
import "@fontsource/frank-ruhl-libre/400.css";
import "@fontsource/frank-ruhl-libre/500.css";
import "./index.css";
import "./i18n";

const AboutUsPage = lazy(() => import("./pages/AboutUsPage"));
const TourismInformations = lazy(() => import("./pages/TourismInformations"));
const StructureOverview = lazy(() => import("./pages/structures/StructureOverview"));
const RoomPage = lazy(() => import("./pages/structures/RoomPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const CinqueTerrePage = lazy(() => import("./pages/CinqueTerrePage"));
const CamerePage = lazy(() => import("./pages/CamerePage"));
const CheckInPage = lazy(() => import("./pages/CheckInPage"));
const EmergenzaPage = lazy(() => import("./pages/EmergenzaPage"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

const GA_ID = "G-29M40P9VVT";
const CONSENT_KEY = "nm_analytics_consent";

// Initialize GA4 only if the user has already given consent in a previous session
if (localStorage.getItem(CONSENT_KEY) === "accepted") {
  ReactGA.initialize(GA_ID);
}

function GATracker() {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.startsWith("/checkin")) return;
    if (localStorage.getItem(CONSENT_KEY) !== "accepted") return;
    ReactGA.send({ hitType: "pageview", page: location.pathname });
  }, [location]);
  return null;
}

if (process.env.NODE_ENV === 'development') {
  window.addEventListener('error', function(event) {
    if (event.message === 'Script error.' && event.filename === '') {
      event.stopImmediatePropagation();
      event.preventDefault();
      return true;
    }
  }, true);
}

/**
 * Cambio pagina in dissolvenza. La pagina vecchia sfuma via, poi entra la
 * nuova: senza questo il passaggio sembra un salto istantaneo.
 */
function RotteInDissolvenza() {
  const location = useLocation();

  // Niente initial={false} su AnimatePresence: quel flag scende a tutti i
  // figli e spegneva l'animazione d'ingresso dell'hero al primo caricamento.
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.28, ease: "easeOut" }}
      >
        <Suspense fallback={<div className="min-h-screen bg-lux-bg" />}>
          <Routes location={location}>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutUsPage />} />
            <Route path="/tourist-informations" element={<TourismInformations />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/cinque-terre" element={<CinqueTerrePage />} />
            <Route path="/camere" element={<CamerePage />} />
            <Route path="/structures/:structureId" element={<StructureOverview />} />
            <Route path="/structures/:structureId/:roomId" element={<RoomPage />} />
            <Route path="/checkin/:slug" element={<CheckInPage />} />
            <Route path="/emergenza/:struttura" element={<EmergenzaPage />} />
            <Route path="/termini-e-condizioni" element={<TermsAndConditions />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Entry />
    {/* In prova: le animazioni restano attive anche quando il sistema chiede
        di ridurre il movimento, per capire se era quello a spegnerle. */}
    <MotionConfig reducedMotion="never">
    {/* Questo Suspense copre anche header e banner: con le lingue caricate
        a richiesta, qualsiasi componente puo' restare in attesa del testo. */}
    <Suspense fallback={<div className="min-h-screen bg-lux-bg" />}>
    <BrowserRouter>
      <GATracker />
      <ScrollToTop />
      <CookieBanner
        onAccept={() => {
          ReactGA.initialize(GA_ID);
          ReactGA.send({ hitType: "pageview", page: window.location.pathname });
        }}
      />
      <RotteInDissolvenza />
    </BrowserRouter>
    </Suspense>
    </MotionConfig>
    <Analytics />
  </React.StrictMode>
);
